import { useState, useEffect, useRef, useCallback } from "react";
import { onrampApi, OnrampQuote } from "@/api/onramp";
import {
  useCoinbaseTransactionStatus,
  CoinbaseTransaction,
} from "@/hooks/useCoinbaseTransactionStatus";
import { triggerGlobalToast } from "@/contexts/ToastContext";

interface DepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress: string;
  onSuccess?: () => void;
}

type DepositStep = "method-select" | "onramp" | "wallet-address";
type OnrampState = "idle" | "loading" | "success" | "error" | "polling";
type CloseType = "none" | "button" | "drag";

const MIN_AMOUNT = 5;
const MAX_AMOUNT = 500;
const DISMISS_THRESHOLD = 150;
const ANIMATION_DURATION = 300;

export function DepositModal({
  isOpen,
  onClose,
  walletAddress,
  onSuccess,
}: DepositModalProps) {
  // Step management
  const [step, setStep] = useState<DepositStep>("method-select");

  // Onramp state
  const [amount, setAmount] = useState("25");
  const [onrampState, setOnrampState] = useState<OnrampState>("idle");
  const [quote, setQuote] = useState<OnrampQuote | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Wallet address copy state
  const [copied, setCopied] = useState(false);

  // Polling state - start polling after Coinbase Pay opens
  const [shouldPoll, setShouldPoll] = useState(false);

  // Transaction polling hook - callbacks handle success/failure
  useCoinbaseTransactionStatus("onramp", shouldPoll, {
    pollInterval: 5000,
    maxPollDuration: 5 * 60 * 1000, // 5 minutes
    onSuccess: (tx: CoinbaseTransaction) => {
      const amount = tx.cryptoAmount || tx.fiatAmount || "";
      triggerGlobalToast(
        `Deposit successful! ${amount} USDC added to your wallet`,
        "success"
      );
      setShouldPoll(false);
      setOnrampState("idle");
      if (onSuccess) {
        onSuccess();
      }
      handleClose("button");
    },
    onFailed: (tx: CoinbaseTransaction) => {
      const reason = tx.failureReason || "Please try again";
      triggerGlobalToast(`Deposit failed: ${reason}`, "error");
      setShouldPoll(false);
      setOnrampState("error");
      setErrorMessage(reason);
    },
  });

  // Animation state
  const [closeType, setCloseType] = useState<CloseType>("none");
  const [dragY, setDragY] = useState(0);
  const [canDrag, setCanDrag] = useState(false);

  // Ref for synchronous drag tracking
  const dragRef = useRef({ active: false, startY: 0 });

  // Track previous isOpen to detect reopen with stale state
  const prevIsOpenRef = useRef(isOpen);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep("method-select");
      setAmount("25");
      setOnrampState("idle");
      setQuote(null);
      setErrorMessage("");
      setCopied(false);
      setShouldPoll(false);
      // Reset animation state
      setCloseType("none");
      setDragY(0);
      setCanDrag(false);
      dragRef.current = { active: false, startY: 0 };
    } else {
      // Stop polling when modal closes
      setShouldPoll(false);
    }
    // Update ref after state is set
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Close handler with animation
  const handleClose = useCallback(
    (type: "button" | "drag" = "button") => {
      if (onrampState === "loading" || closeType !== "none") return;

      // Stop polling when closing
      setShouldPoll(false);

      setCloseType(type);
      if (type === "drag") {
        setDragY(window.innerHeight);
      }
      setTimeout(onClose, ANIMATION_DURATION);
    },
    [onrampState, closeType, onClose]
  );

  // Cancel polling and reset to allow retry
  const handleCancelPolling = useCallback(() => {
    setShouldPoll(false);
    setOnrampState("idle");
  }, []);

  // Pointer event handlers for drag gesture
  const handlePointerDown = (e: React.PointerEvent) => {
    if (onrampState === "loading" || !canDrag) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { active: true, startY: e.clientY };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    const newDragY = Math.max(0, e.clientY - dragRef.current.startY);
    setDragY(newDragY);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    e.preventDefault();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    dragRef.current.active = false;

    if (dragY > DISMISS_THRESHOLD) {
      handleClose("drag");
    } else {
      setDragY(0);
    }
  };

  // Don't render if modal is closed
  if (!isOpen) return null;

  // Detect if modal just reopened (isOpen went from false to true)
  const justReopened = isOpen && !prevIsOpenRef.current;

  // Prevent flash: if just reopened but has stale close state, wait for useEffect to reset
  // This happens when user closes then quickly reopens - closeType is still "button"/"drag"
  // But during legitimate close animation, prevIsOpenRef.current is true, so we render normally
  if (justReopened && closeType !== "none") {
    return null;
  }

  // Derived state
  const isClosing = closeType !== "none";
  const isDragging = dragRef.current.active;
  const numericAmount = parseFloat(amount) || 0;
  const isValidAmount =
    numericAmount >= MIN_AMOUNT && numericAmount <= MAX_AMOUNT;

  // Handle wallet address copy
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy address:", error);
    }
  };

  // Handle onramp generation
  const handleGenerateOnramp = async () => {
    if (!isValidAmount || onrampState === "loading") return;

    setOnrampState("loading");
    setErrorMessage("");

    try {
      const result = await onrampApi.createUrl(numericAmount);
      setQuote(result.quote);
      setOnrampState("success");

      // Detect mobile/PWA environment
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      const isPWA = window.matchMedia("(display-mode: standalone)").matches;

      // On mobile or PWA, use full-page navigation instead of popup
      // This avoids popup blocking and provides better UX
      if (isMobile || isPWA) {
        window.location.href = result.onrampUrl;
        // Note: For mobile redirect, polling will resume when user returns
        // The app should handle this via session storage or URL params
      } else {
        // Desktop: Open in centered popup window
        const width = 500;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(
          result.onrampUrl,
          "coinbase-onramp",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          console.warn(
            "Popup was blocked, falling back to full-page navigation"
          );
          window.location.href = result.onrampUrl;
          return;
        }

        // Start polling for transaction status
        setOnrampState("polling");
        setShouldPoll(true);
      }
    } catch (error: any) {
      console.error("Failed to generate onramp URL:", error);
      setOnrampState("error");
      setErrorMessage(
        error.response?.data?.error ||
          "Failed to generate deposit link. Please try again."
      );
    }
  };

  // Handle back navigation
  const handleBack = () => {
    setStep("method-select");
    setOnrampState("idle");
    setQuote(null);
    setErrorMessage("");
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        style={{
          opacity: dragY > 0 ? Math.max(0, 1 - dragY / 300) : undefined,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose("button");
        }}
      />

      {/* Modal Container */}
      <div
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center sm:items-center sm:inset-0 sm:p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`w-full max-w-[440px] bg-[#102210] sm:rounded-[2.5rem] rounded-t-[2.5rem] relative flex flex-col max-h-[92vh] sm:max-h-[90vh] shadow-2xl ring-1 ring-white/10 overflow-hidden ${
            closeType === "drag"
              ? ""
              : closeType === "button"
              ? "animate-slide-down"
              : canDrag
              ? ""
              : "animate-slide-up"
          }`}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? "none" : "transform 0.3s ease-out",
          }}
          onAnimationEnd={() => {
            if (!isClosing) setCanDrag(true);
          }}
        >
          {/* Drag Handle */}
          <div
            className="flex justify-center pt-6 pb-4 shrink-0 cursor-grab active:cursor-grabbing select-none"
            style={{ touchAction: "none" }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <div className="h-1.5 w-14 rounded-full bg-white/20 hover:bg-white/30 transition-colors pointer-events-none" />
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 pt-2 pb-8 scrollbar-hide">
            {/* Back button for sub-steps */}
            {step !== "method-select" && (
              <button
                onClick={handleBack}
                className="absolute top-6 left-6 z-20 flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all"
              >
                <span className="material-symbols-outlined text-xl">
                  arrow_back
                </span>
              </button>
            )}

            {/* Step 1: Method Selection */}
            {step === "method-select" && (
              <div className="flex flex-col items-center text-center mt-2">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                  <div className="relative w-24 h-24 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10 shadow-[0_8px_16px_-4px_rgba(0,0,0,0.5)]">
                    <span className="material-symbols-outlined text-primary text-[40px]">
                      account_balance_wallet
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-white tracking-tight text-[28px] font-extrabold leading-tight mb-3">
                  Deposit USDC
                </h2>

                {/* Description */}
                <p className="text-[#90cb90]/70 text-sm mb-10 font-medium">
                  Choose your deposit method
                </p>

                {/* Method Options */}
                <div className="w-full space-y-4">
                  {/* Option 1: Buy with Card/Apple Pay */}
                  <button
                    onClick={() => setStep("onramp")}
                    className="w-full group flex items-center text-left p-4 pr-5 rounded-2xl border border-white/10 bg-[#182e18] hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent to-transparent group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                    <div className="shrink-0 mr-4 relative z-10">
                      <div className="w-12 h-12 rounded-xl bg-[#224922] flex items-center justify-center text-primary ring-1 ring-white/5 group-hover:scale-105 transition-transform duration-300">
                        <span className="material-symbols-outlined">
                          credit_card
                        </span>
                      </div>
                    </div>
                    <div className="grow relative z-10">
                      <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">
                        Buy with Card or Apple Pay
                      </h3>
                      <p className="text-xs text-[#90cb90]/70 mb-1">
                        Instant & Secure
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-primary/70">
                        Powered by Coinbase
                      </p>
                    </div>
                    <div className="shrink-0 ml-2 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all relative z-10">
                      <span className="material-symbols-outlined text-[22px]">
                        arrow_forward
                      </span>
                    </div>
                  </button>

                  {/* Option 2: Transfer from Wallet */}
                  <button
                    onClick={() => setStep("wallet-address")}
                    className="w-full group flex items-center text-left p-4 pr-5 rounded-2xl border border-white/10 bg-[#182e18] hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent to-transparent group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                    <div className="shrink-0 mr-4 relative z-10">
                      <div className="w-12 h-12 rounded-xl bg-[#224922] flex items-center justify-center text-primary ring-1 ring-white/5 group-hover:scale-105 transition-transform duration-300">
                        <span className="material-symbols-outlined">
                          account_balance_wallet
                        </span>
                      </div>
                    </div>
                    <div className="grow relative z-10">
                      <h3 className="font-semibold text-white mb-1 group-hover:text-primary transition-colors">
                        Transfer from Wallet
                      </h3>
                      <p className="text-xs text-[#90cb90]/70 mb-1">
                        Send from another crypto wallet
                      </p>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-primary/70">
                        No network fees
                      </p>
                    </div>
                    <div className="shrink-0 ml-2 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all relative z-10">
                      <span className="material-symbols-outlined text-[22px]">
                        arrow_forward
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 2a: Onramp (Buy with Card) */}
            {step === "onramp" && (
              <div className="flex flex-col items-center text-center mt-8">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                  <div className="relative w-20 h-20 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10">
                    <span className="material-symbols-outlined text-primary text-[36px]">
                      credit_card
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-white tracking-tight text-[24px] font-extrabold leading-tight mb-2">
                  Buy USDC
                </h2>
                <p className="text-[#90cb90]/70 text-sm mb-8">
                  Enter amount to purchase via Coinbase Pay
                </p>

                {/* Amount Input */}
                <div className="w-full mb-6">
                  <div className="flex justify-between items-end px-1 mb-2">
                    <label className="text-white text-sm font-bold">
                      Amount (USD)
                    </label>
                    <span className="text-xs font-semibold text-[#90cb90] bg-white/5 px-2 py-0.5 rounded-md">
                      ${MIN_AMOUNT} - ${MAX_AMOUNT}
                    </span>
                  </div>
                  <div className="relative flex items-center w-full rounded-xl bg-[#224922] border border-transparent focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/50 transition-all duration-300 overflow-hidden">
                    <div className="pl-4 text-white/40 text-xl font-semibold">
                      $
                    </div>
                    <input
                      type="number"
                      min={MIN_AMOUNT}
                      max={MAX_AMOUNT}
                      step="1"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={onrampState === "loading"}
                      placeholder="25"
                      className="w-full bg-transparent border-none text-white focus:ring-0 h-14 placeholder:text-white/20 p-3 pl-1 text-2xl font-bold tracking-tight [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Quote Preview */}
                {quote && onrampState === "success" && (
                  <div className="w-full mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
                    <p className="text-white text-xs font-semibold mb-3 text-left">
                      Fee Breakdown
                    </p>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-[#90cb90]/70">
                          You'll receive
                        </span>
                        <span className="text-white font-semibold">
                          {quote.usdcAmount} USDC
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#90cb90]/70">Network fee</span>
                        <span className="text-white">${quote.networkFee}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#90cb90]/70">Coinbase fee</span>
                        <span className="text-white">${quote.coinbaseFee}</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                        <span className="text-[#90cb90]">Total</span>
                        <span className="text-white">${quote.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {onrampState === "error" && errorMessage && (
                  <div className="w-full mb-6 bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex gap-3">
                    <span className="material-symbols-outlined text-red-400 shrink-0 text-xl">
                      error
                    </span>
                    <p className="text-red-300 text-xs leading-relaxed">
                      {errorMessage}
                    </p>
                  </div>
                )}

                {/* Info Banner */}
                <div className="w-full mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3">
                  <span className="material-symbols-outlined text-blue-400 shrink-0 text-xl">
                    info
                  </span>
                  <div className="text-white/70 text-xs leading-relaxed text-left">
                    <p className="mb-1">✓ Pay with debit card or Apple Pay</p>
                    <p className="mb-1">✓ No cash cards accepted</p>
                    <p className="mb-1">
                      ✓ USDC delivered to your Dasher account
                    </p>
                    <p>✓ Secured by Coinbase</p>
                  </div>
                </div>

                {/* Polling Status Banner */}
                {onrampState === "polling" && (
                  <div className="w-full mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex gap-3 items-center">
                      <span className="material-symbols-outlined text-blue-400 animate-spin shrink-0 text-xl">
                        sync
                      </span>
                      <div className="text-white/90 text-sm leading-relaxed flex-1">
                        <p className="font-semibold">
                          Waiting for payment confirmation...
                        </p>
                        <p className="text-xs text-white/60 mt-1">
                          Complete your payment in the Coinbase Pay window
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelPolling}
                      className="mt-3 w-full text-sm text-white/50 hover:text-white/70 transition-colors py-2"
                    >
                      Cancel and try again
                    </button>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={handleGenerateOnramp}
                  disabled={
                    !isValidAmount ||
                    onrampState === "loading" ||
                    onrampState === "polling"
                  }
                  className={`w-full group relative font-extrabold text-lg h-[60px] rounded-2xl transition-all active:scale-[0.98] overflow-hidden flex items-center justify-center gap-3 ${
                    isValidAmount &&
                    onrampState !== "loading" &&
                    onrampState !== "polling"
                      ? "bg-primary hover:bg-primary/90 text-[#102210] shadow-[0_0_25px_-5px_rgba(13,242,13,0.4)]"
                      : "bg-white/10 text-white/40 cursor-not-allowed"
                  }`}
                >
                  {onrampState === "loading" ? (
                    <>
                      <span className="material-symbols-outlined animate-spin">
                        sync
                      </span>
                      <span>Generating link...</span>
                    </>
                  ) : onrampState === "polling" ? (
                    <>
                      <span className="material-symbols-outlined animate-pulse">
                        hourglass_top
                      </span>
                      <span>Waiting for confirmation...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue to Coinbase Pay</span>
                      <span className="material-symbols-outlined transition-transform group-hover:translate-x-1">
                        arrow_forward
                      </span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Step 2b: Wallet Address (Transfer from Wallet) */}
            {step === "wallet-address" && (
              <div className="flex flex-col items-center text-center mt-8">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute -inset-4 bg-primary/20 rounded-full blur-xl opacity-40" />
                  <div className="relative w-20 h-20 bg-gradient-to-b from-[#1a381a] to-[#112411] rounded-full flex items-center justify-center ring-1 ring-white/10">
                    <span className="material-symbols-outlined text-primary text-[36px]">
                      account_balance_wallet
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-white tracking-tight text-[24px] font-extrabold leading-tight mb-2">
                  Deposit USDC
                </h2>
                <p className="text-[#90cb90]/70 text-sm mb-8">
                  Send USDC on Base Network to your wallet
                </p>

                {/* Wallet Address Card */}
                <div className="flex flex-col gap-3 mb-6 w-full">
                  <div className="rounded-2xl border border-white/10 bg-[#182e18] p-4">
                    <p className="text-[#90cb90]/70 text-xs font-semibold uppercase tracking-wider mb-3 text-left">
                      Your Wallet Address
                    </p>
                    <p className="text-white text-sm font-mono break-all leading-relaxed text-left">
                      {walletAddress}
                    </p>
                  </div>

                  {/* Copy Button */}
                  <button
                    onClick={handleCopy}
                    className="flex items-center justify-center gap-2 h-12 rounded-xl bg-primary/20 text-primary font-bold hover:bg-primary/30 transition-all"
                  >
                    <span className="material-symbols-outlined text-xl">
                      {copied ? "check" : "content_copy"}
                    </span>
                    <span>{copied ? "Copied!" : "Copy Address"}</span>
                  </button>
                </div>

                {/* Warning Notice */}
                <div className="w-full mb-6 bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3 items-start">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-full shrink-0 ring-1 ring-amber-500/30 flex items-center justify-center">
                    <span
                      className="material-symbols-outlined text-amber-400 text-xl"
                      style={{ fontVariationSettings: "'FILL' 1" }}
                    >
                      warning
                    </span>
                  </div>
                  <div>
                    <h4 className="text-amber-400 text-xs font-bold uppercase tracking-wide mb-1">
                      Network Warning
                    </h4>
                    <p className="text-amber-200/90 text-xs leading-relaxed">
                      Only send{" "}
                      <span className="font-semibold text-amber-400">
                        USDC on Base Network
                      </span>
                      . Other tokens or networks may result in loss of funds.
                    </p>
                  </div>
                </div>

                {/* Done Button */}
                <button
                  onClick={() => handleClose("button")}
                  className="w-full group relative font-extrabold text-lg h-[60px] rounded-2xl bg-primary hover:bg-primary/90 text-[#102210] shadow-[0_0_25px_-5px_rgba(13,242,13,0.4)] transition-all active:scale-[0.98] flex items-center justify-center"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
